import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  GridLegacy as Grid,
  Radio,
  RadioGroup,
  Stack,
  TextField,
} from '@mui/material';

import DateTimeWidget from '~/components/Common/Elements/DateTimeWidget';
import Spinner from '~/components/Common/Spinner';
import InputCruiseLineSelect from '~/components/Cruises/components/Selects/InputCruiseLineSelect';
import InputShipMultiselect from '~/components/Cruises/components/Selects/InputShipMultiselect';
import { CruiseInclusion } from '~/components/Cruises/pages/Inclusions/types';

import useAppNavigation from '~/hooks/useAppNavigation';

import { CruiseLine } from '~/services/cruises/CruiseLineService';
import inclusionsService from '~/services/cruises/InclusionsService';

import EvergreenShipConflictsModal, { EvergreenShipConflict } from './EvergreenShipConflictsModal';

export const TYPES: { [key: string]: CruiseInclusion['type'] } = {
  STANDARD: 'STANDARD',
  LUX_PLUS: 'LUX_PLUS',
  EVERGREEN: 'EVERGREEN',
};

const INITIAL_FORM_STATE: Partial<CruiseInclusion> = {
  name: '',
  startDate: null,
  endDate: null,
  type: TYPES.STANDARD,
  fkVendorId: null,
  shipIds: [],
};

type Props = {
  inclusion?: CruiseInclusion;
  setSelectedCruiseLineId?: (cruiseLineId: string) => void;
};

function InclusionForm({ inclusion, setSelectedCruiseLineId }: Props) {
  const [status, setStatus] = useState<string>();
  const [formValues, setFormValues] = useState<Partial<CruiseInclusion>>(INITIAL_FORM_STATE);
  const [selectedCruiseLine, setSelectedCruiseLine] = useState<CruiseLine | null>(null);
  const [isShipSelectionEnabled, setIsShipSelectionEnabled] = useState(false);
  const [shipConflicts, setShipConflicts] = useState<Array<EvergreenShipConflict>>([]);

  const { enqueueSnackbar } = useSnackbar();
  const { push } = useAppNavigation();
  const isEditing = !!inclusion;
  const isEvergreen = formValues.type === TYPES.EVERGREEN;

  const isSaveEnabled = useMemo(
    () =>
      formValues.name &&
      (isEvergreen || (formValues.startDate && formValues.endDate)) &&
      formValues.type &&
      formValues.fkVendorId &&
      (!isEvergreen || !isShipSelectionEnabled || formValues.shipIds?.length) &&
      status !== 'loading',
    [formValues, isEvergreen, isShipSelectionEnabled, status],
  );

  const handleChangeField = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      event.persist();

      if (event?.target?.name) {
        setFormValues({
          ...formValues,
          [event.target.name]: event.target.value ?? undefined,
        });
      }
    },
    [formValues],
  );

  const handleChangeType = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const type = event.target.value as CruiseInclusion['type'];

      setFormValues({
        ...formValues,
        type,
        startDate: type === TYPES.EVERGREEN ? null : formValues.startDate,
        endDate: type === TYPES.EVERGREEN ? null : formValues.endDate,
        shipIds: type === TYPES.EVERGREEN ? formValues.shipIds : [],
      });
    },
    [formValues],
  );

  const handleChangeDateField = useCallback(
    (value: string, name: 'startDate' | 'endDate') => {
      setFormValues({ ...formValues, [name]: value });
    },
    [formValues],
  );

  const handleCruiseLineField = useCallback(
    (cruiseLine: CruiseLine) => {
      const isNewCruiseLine = cruiseLine?.id !== formValues.fkVendorId;
      setFormValues({
        ...formValues,
        fkVendorId: cruiseLine?.id ?? null,
        shipIds: isNewCruiseLine ? [] : formValues.shipIds,
      });
      setSelectedCruiseLine(cruiseLine);
      if (isNewCruiseLine) {
        setIsShipSelectionEnabled(false);
      }
      setSelectedCruiseLineId?.(cruiseLine?.id ?? null);
    },
    [formValues, setSelectedCruiseLineId],
  );

  const handleShipScopeChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const shouldSelectShips = event.target.value === 'selected';
    setIsShipSelectionEnabled(shouldSelectShips);
    if (!shouldSelectShips) {
      setFormValues((previousValues) => ({ ...previousValues, shipIds: [] }));
    }
  }, []);

  const handleShipsChange = useCallback((shipIds: Array<string>) => {
    setFormValues((previousValues) => ({ ...previousValues, shipIds }));
  }, []);

  const handleSubmit = useCallback(async () => {
    let inclusionId = inclusion?.id;
    setStatus('loading');

    try {
      const payload = { ...formValues };

      if (payload.type === TYPES.EVERGREEN) {
        delete payload.startDate;
        delete payload.endDate;
      }

      if (isEditing) {
        await inclusionsService.update(inclusion.id, payload);
      } else {
        const newInclusion = await inclusionsService.create(payload);
        inclusionId = newInclusion.id;
      }

      setStatus('success');
      enqueueSnackbar('Inclusion saved with success', { variant: 'success' });
      push(`/cruises/inclusions/edit/${inclusionId}/items`);
    } catch (error) {
      const conflicts = (error as { responseBody?: { shipConflicts?: Array<EvergreenShipConflict> } }).responseBody
        ?.shipConflicts;

      if (conflicts?.length) {
        setShipConflicts(conflicts);
        setStatus('idle');
        return;
      }

      console.error(error);
      setStatus('error');
      const errorMessage = error instanceof Error ? error.message : 'Error saving inclusion';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  }, [inclusion?.id, isEditing, enqueueSnackbar, push, formValues]);

  useEffect(() => {
    if (inclusion) {
      setFormValues({
        name: inclusion.name,
        startDate: inclusion.startDate,
        endDate: inclusion.endDate,
        type: inclusion.type,
        fkVendorId: inclusion.fkVendorId,
        shipIds: inclusion.shipIds,
      });
      setIsShipSelectionEnabled(inclusion.shipIds.length > 0);
    }
  }, [inclusion]);

  return (
    <Box>
      <EvergreenShipConflictsModal conflicts={shipConflicts} onClose={() => setShipConflicts([])} />
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            required
            fullWidth
            name="name"
            label="Inclusion Name"
            onChange={handleChangeField}
            inputProps={{ maxLength: 50 }}
            value={formValues.name}
            helperText="The maximum length is 50 characters."
          />
        </Grid>
        <Grid item xs={6}>
          <InputCruiseLineSelect
            label="Cruise line"
            value={formValues.fkVendorId}
            onChange={handleCruiseLineField}
            withApprovedOffers
            disabled={status === 'loading'}
          />
        </Grid>
      </Grid>

      <Box mt={2}>
        <FormControl>
          <RadioGroup defaultValue={TYPES.STANDARD} name="type" value={formValues.type} onChange={handleChangeType} row>
            <FormControlLabel disabled={isEditing} value={TYPES.STANDARD} control={<Radio />} label="Standard" />
            <FormControlLabel disabled={isEditing} value={TYPES.LUX_PLUS} control={<Radio />} label="Lux Plus" />
            <FormControlLabel disabled={isEditing} value={TYPES.EVERGREEN} control={<Radio />} label="Evergreen" />
          </RadioGroup>
        </FormControl>
      </Box>

      {isEvergreen && (
        <Box mt={2}>
          <FormControl>
            <RadioGroup
              name="shipScope"
              value={isShipSelectionEnabled ? 'selected' : 'all'}
              onChange={handleShipScopeChange}
              row
            >
              <FormControlLabel value="all" control={<Radio />} label="All ships" />
              <FormControlLabel value="selected" control={<Radio />} label="Select ships" />
            </RadioGroup>
          </FormControl>

          {isShipSelectionEnabled && (
            <Box mt={2}>
              <InputShipMultiselect
                cruiseLineName={selectedCruiseLine?.name}
                shipIds={formValues.shipIds ?? []}
                onChange={handleShipsChange}
                disabled={status === 'loading'}
              />
            </Box>
          )}
        </Box>
      )}

      {!isEvergreen && (
        <Stack mt={2} spacing={2} direction="row">
          <DateTimeWidget
            defaultTimezone="Australia/Sydney"
            disablePast
            format="DD/MM/YYYY HH:mm"
            label="Inclusion Start Date *"
            value={formValues.startDate}
            onChange={(value) => handleChangeDateField(value, 'startDate')}
          />

          <DateTimeWidget
            defaultTimezone="Australia/Sydney"
            disablePast
            format="DD/MM/YYYY HH:mm"
            label="Inclusion End Date *"
            value={formValues.endDate}
            onChange={(value) => handleChangeDateField(value, 'endDate')}
          />
        </Stack>
      )}

      <Box mt={2} display="flex" justifyContent="flex-end">
        <Button variant="contained" onClick={handleSubmit} disabled={!isSaveEnabled}>
          {status === 'loading' && <Spinner size={15} />}
          Save Changes
        </Button>
      </Box>
    </Box>
  );
}

export default InclusionForm;
